import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export const downloadMyMemories = async () => {
  try {
    const res = await api.get("/account/export", { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = "My-memories.pdf"; a.click();
    URL.revokeObjectURL(url);
  } catch {
    toast.error("Could not download your memories. Please try again.");
  }
};

export const AccountDataDialog = ({ open, onOpenChange }) => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [confirming, setConfirming] = useState(false);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const del = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      await api.post("/account/delete", { password });
      logout();
      toast.success("Your account and memories have been permanently deleted.");
      navigate("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) { setConfirming(false); setPassword(""); setError(""); } }}>
      <DialogContent data-testid="account-data-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Your data</DialogTitle>
          <DialogDescription>Download a copy of everything you've written, or permanently delete your account.</DialogDescription>
        </DialogHeader>
        <button data-testid="account-download-btn" onClick={downloadMyMemories} className="w-full rounded-full bg-[#2E3B31] py-2.5 text-sm font-semibold text-[#FBF4E8] hover:bg-[#3a4a3e]">Download my memories (PDF)</button>
        {!confirming ? (
          <button data-testid="account-delete-start" onClick={() => setConfirming(true)} className="w-full rounded-full border border-[#A84A32]/40 py-2.5 text-sm font-semibold text-[#A84A32] hover:bg-[#F7E3DE]">Delete my account now</button>
        ) : (
          <form onSubmit={del} className="rounded-xl border border-[#A84A32]/30 bg-[#FBF1EE] p-4">
            <p className="text-sm leading-relaxed text-[#4A4540]">This permanently deletes your account, memories, reflections and keepsakes. It cannot be undone. Payment records are kept for tax purposes only. Enter your password to confirm.</p>
            <input data-testid="account-delete-password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Your password" className="mt-3 w-full rounded-md border border-[#4A4540]/20 bg-white px-3 py-2 text-sm" />
            {error && <p data-testid="account-delete-error" className="mt-2 text-xs text-[#A84A32]">{error}</p>}
            <button data-testid="account-delete-confirm" type="submit" disabled={busy || !password} className="mt-3 w-full rounded-full bg-[#A84A32] py-2.5 text-sm font-semibold text-white disabled:opacity-60">{busy ? "Deleting…" : "Permanently delete everything"}</button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
